import React, { useState } from "react";
import { toast } from "sonner";
import { Mail, MapPin, Clock } from "lucide-react";
import { submitContact } from "../lib/api";

export default function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast("Please fill in all required fields");
      return;
    }
    setSubmitting(true);
    try {
      await submitContact(form);
      setSuccess(true);
      setForm({ name: "", email: "", subject: "", message: "" });
      toast.success("Message sent — we'll reply within 1 business day.");
    } catch (err) {
      toast("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page" className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-20">
        <div className="md:col-span-5">
          <div className="label-xs mb-3">Contact</div>
          <h1 className="font-display text-5xl md:text-6xl font-medium leading-[0.95] mb-6">
            Get in touch.
          </h1>
          <p className="text-neutral-600 leading-relaxed mb-10 max-w-md">
            Questions about sizing, orders, or returns? Our small team is
            happy to help — we reply to every email personally, usually within
            one business day.
          </p>

          <div className="space-y-5 text-sm">
            <InfoRow icon={Mail} label="Email" value="support@simplestylemode.com" />
            <InfoRow
              icon={MapPin}
              label="Address"
              value={<>ULTRANEQ LLC<br />2105 Vista Oeste NW<br />Albuquerque, NM 87120, USA</>}
            />
            <InfoRow icon={Clock} label="Hours" value="Mon–Fri, 9:00–17:00 MT" />
          </div>
        </div>

        <div className="md:col-span-7">
          {success ? (
            <div
              data-testid="contact-success"
              className="border border-[color:var(--ssm-border)] p-10 bg-[color:var(--ssm-surface)]"
            >
              <h2 className="font-display text-3xl mb-2">Thank you.</h2>
              <p className="text-neutral-600">
                Your message has been received. We'll get back to you at the
                email you provided within one business day.
              </p>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="space-y-8" data-testid="contact-form">
              <FieldRow>
                <FormField label="Name" required>
                  <input
                    required
                    data-testid="contact-name"
                    value={form.name}
                    onChange={onChange("name")}
                    className="w-full border-b border-neutral-300 pb-2 bg-transparent focus:outline-none focus:border-black transition-colors"
                  />
                </FormField>
                <FormField label="Email" required>
                  <input
                    required
                    type="email"
                    data-testid="contact-email"
                    value={form.email}
                    onChange={onChange("email")}
                    className="w-full border-b border-neutral-300 pb-2 bg-transparent focus:outline-none focus:border-black transition-colors"
                  />
                </FormField>
              </FieldRow>
              <FormField label="Subject">
                <input
                  data-testid="contact-subject"
                  value={form.subject}
                  onChange={onChange("subject")}
                  className="w-full border-b border-neutral-300 pb-2 bg-transparent focus:outline-none focus:border-black transition-colors"
                />
              </FormField>
              <FormField label="Message" required>
                <textarea
                  required
                  rows={6}
                  data-testid="contact-message"
                  value={form.message}
                  onChange={onChange("message")}
                  className="w-full border-b border-neutral-300 pb-2 bg-transparent focus:outline-none focus:border-black transition-colors resize-none"
                />
              </FormField>

              <button
                type="submit"
                disabled={submitting}
                data-testid="contact-submit-btn"
                className="inline-flex items-center px-10 py-4 bg-black text-white text-xs uppercase tracking-[0.25em] hover:bg-neutral-800 transition-colors disabled:opacity-60"
              >
                {submitting ? "Sending…" : "Send Message"}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

const InfoRow = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-4">
    <Icon size={16} strokeWidth={1.5} className="mt-0.5" />
    <div>
      <div className="label-xs mb-1">{label}</div>
      <div className="text-neutral-700">{value}</div>
    </div>
  </div>
);

const FieldRow = ({ children }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">{children}</div>
);

const FormField = ({ label, required, children }) => (
  <label className="block">
    <div className="label-xs mb-2">
      {label}
      {required && <span className="ml-1">*</span>}
    </div>
    {children}
  </label>
);
